import type { ProximityLocationBridge } from '../engine/locationBridge';
import type { LynxPublishEvent } from '../native/locationDefinitions';

import type { PublishStatus, PublisherEvent, PositionPublisher } from './publishPosition';

/**
 * The publisher, backed by Swift.
 *
 * The JS implementation in publishPosition.ts is a setInterval, which only runs while the
 * app is on screen. iOS keeps this process alive for background location — the blue
 * indicator is CoreLocation saying so — but WebKit suspends the web view independently of
 * the host app's background entitlement. Measured on device 2026-09-06: location actively
 * delivering, and `client_ts` showed zero JS events across a 5m24s screen lock. Publishing
 * had to leave JavaScript entirely for Find to work with a phone in a pocket.
 *
 * This adapter keeps the PositionPublisher shape so the session code, the Find logging and
 * the radar's publishStatus are unchanged; only the engine underneath moved. evaluatePublishGate
 * stays in publishPosition.ts as the reference implementation and the place the rules are
 * unit-tested — LynxPublishRules in LynxLocationPlugin.swift mirrors it, and the two should
 * change together.
 */

export type NativePositionPublisherOptions = {
  bridge: ProximityLocationBridge;
  supabaseUrl: string;
  anonKey: string;
  userId: string;
  /** Resolves the current session. Called on every start so a resumed app re-arms Swift. */
  getTokens: () => Promise<{ accessToken: string | null; refreshToken: string | null }>;
  onEvent?: (event: PublisherEvent) => void;
};

/** Native emits the same shapes as the JS publisher, minus fields Swift has no view of. */
function toPublisherEvent(event: LynxPublishEvent): PublisherEvent | null {
  switch (event.type) {
    case 'published':
      return {
        type: 'published',
        reason: event.reason,
        movedMeters: event.movedMeters,
        first: event.first,
      };
    case 'gated':
      return {
        type: 'gated',
        reason: event.reason,
        accuracyMeters: event.accuracyMeters,
        ageMs: event.ageMs,
      };
    case 'error':
      return { type: 'error', code: event.code, message: event.message };
    case 'no_fix':
      // Swift has no session start time to measure from; 0 reads as "since the last tick",
      // and the JS caller only warns past a threshold, so this stays quiet rather than noisy.
      return { type: 'no_fix', sinceStartMs: 0 };
    case 'token_refreshed':
      return { type: 'token_refreshed' };
    default:
      return null;
  }
}

export function createNativePositionPublisher(
  options: NativePositionPublisherOptions,
): PositionPublisher {
  const { bridge, supabaseUrl, anonKey, userId, getTokens, onEvent } = options;

  let unsubscribe: (() => void | Promise<void>) | null = null;
  let started = false;
  let status: PublishStatus = {
    active: false,
    lastPublishedAt: null,
    lastOk: false,
    hasGpsReady: false,
    hasEverPublishedSuccessfully: false,
    lastErrorCode: null,
  };

  async function attachListener(): Promise<void> {
    if (unsubscribe) {
      return;
    }
    unsubscribe = await bridge.addPublishEventListener((event) => {
      // Mirror Swift's state locally so getStatus() stays synchronous for the radar.
      if (event.type === 'published') {
        status = {
          ...status,
          lastOk: true,
          lastErrorCode: null,
          hasGpsReady: true,
          hasEverPublishedSuccessfully: true,
          lastPublishedAt: Date.now(),
        };
      } else if (event.type === 'error') {
        status = { ...status, lastOk: false, lastErrorCode: event.code };
      } else if (event.type === 'gated') {
        status = { ...status, hasGpsReady: true };
      }
      const mapped = toPublisherEvent(event);
      if (mapped) {
        onEvent?.(mapped);
      }
    });
  }

  return {
    start(groupId, opts) {
      if (started) {
        return;
      }
      started = true;
      status = { ...status, active: true };
      void (async () => {
        await attachListener();
        const { accessToken, refreshToken } = await getTokens();
        if (!accessToken) {
          status = { ...status, lastOk: false, lastErrorCode: 'no_auth_token' };
          onEvent?.({
            type: 'error',
            code: 'no_auth_token',
            message: 'no access token available for native publishing',
          });
          return;
        }
        await bridge.startPublishing({
          supabaseUrl,
          anonKey,
          accessToken,
          refreshToken: refreshToken ?? undefined,
          userId,
          groupId,
          ...(opts?.liveUntilMs ? { liveUntilMs: opts.liveUntilMs } : {}),
          ...(opts?.venue
            ? {
                venueLat: opts.venue.lat,
                venueLng: opts.venue.lng,
                venueRadiusM: opts.venue.radiusM,
              }
            : {}),
        });
      })();
    },

    stop() {
      started = false;
      status = { ...status, active: false };
      void bridge.stopPublishing().catch(() => {});
      const unsub = unsubscribe;
      unsubscribe = null;
      if (unsub) {
        void Promise.resolve(unsub()).catch(() => {});
      }
    },

    getStatus() {
      return status;
    },

    onPositionUpdate() {
      // Swift is fed straight from didUpdateLocations, so there is nothing to poke. The
      // method stays for interface parity with the JS publisher.
    },
  };
}

/** Push a refreshed session down to Swift, which cannot see the web layer's auth state. */
export async function syncNativePublishTokens(
  bridge: ProximityLocationBridge,
  tokens: { accessToken: string | null; refreshToken: string | null },
): Promise<void> {
  if (!tokens.accessToken) {
    return;
  }
  try {
    await bridge.updatePublishTokens({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken ?? undefined,
    });
  } catch {
    // Swift refreshes on its own after a 401; this is an optimisation, not a requirement.
  }
}
